<template>
  <activity-item-wrapper :type="CommunicationType.sms" :actions>
    <template #content>
      <activity-content :content="item.message" />
      <sms-footer :item />
      <log-comments-section
        v-if="showComments"
        :comments="item.comments ?? []"
        :version="2"
      />
      <send-deal-communication-modal
        v-if="showReplyModal"
        :deal-id="activeDeal.id"
        :type="CommunicationType.sms"
        :deal-name="activeDeal.business?.business_legal_name"
        :reply-to-message="item.message"
        @close-dialog="showReplyModal = false"
      />
    </template>
  </activity-item-wrapper>
</template>
<script setup lang="ts">
import { computed, provide, ref } from "vue";
import { useI18n } from "vue-i18n";

import { CommunicationType, LogEndpointType } from "@/enums/communicationLogs";
import { CommunicationLogContextKey } from "@/helpers/symbols";
import { useDealsBase } from "@/hooks/deals";

import SendDealCommunicationModal from "@/components/communication/SendDealCommunicationModal.vue";
import LogCommentsSection from "@/components/communicationLogs/comments/LogCommentsSection.vue";
import LogCommentsToggle from "@/components/communicationLogs/comments/LogCommentsToggle.vue";
import ActivityContent from "@/components/deals/dealActivity/ActivityContent.vue";
import ActivityItemWrapper from "@/components/deals/dealActivity/ActivityItemWrapper.vue";
import SmsFooter from "@/components/deals/dealActivity/sms/SmsFooter.vue";
import IconReply from "@/components/icons/IconReply.vue";

import type { SmsLogData } from "@/models/communicationLogs";
import type { ActivityAction } from "@/models/dealActivity";

const { item } = defineProps<{
  item: SmsLogData;
}>();

provide(CommunicationLogContextKey, {
  type: LogEndpointType.Sms,
  logId: item.id,
});

const { t } = useI18n();
const { activeDeal } = useDealsBase();

const showComments = ref(false);
const showReplyModal = ref(false);

const actions = computed(() => {
  const options: ActivityAction[] = [
    {
      name: "comments",
      component: LogCommentsToggle,
      props: {
        count: item.comments?.length ?? 0,
        modelValue: showComments.value,
      },
      tooltip: showComments.value
        ? `${t("COMMON.HIDE")} ${t("ACTIVITY.COMMENTS.TITLE", 2)}`
        : `${t("COMMON.SEE")} ${t("ACTIVITY.COMMENTS.TITLE", 2)}`,
      click: () => {
        showComments.value = !showComments.value;
      },
    },
  ];

  if (!item.is_outgoing) {
    options.unshift({
      name: "reply",
      icon: IconReply,
      tooltip: t("COMMON.REPLY"),
      class: "hover:bg-gray-50",
      click: () => {
        showReplyModal.value = true;
      },
    });
  }

  return options;
});
</script>
